<script lang="ts">
  import { cx } from '@emotion/css';
  import { ResponsiveVars } from '@twist/core';
  import type { ColorPalette } from '../utils/color';
  import type { Interaction, InteractionStore } from '../utils/interactions';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { isChartLarge } from '../utils/chart';

  interface Props {
    categories: string[];
    colorPalette: ColorPalette;
    interactions?: Interaction[];
    legendDisplay?: string;
    store?: InteractionStore | null;
    gridColspan: number;
  }

  let {
    categories,
    colorPalette,
    interactions = [],
    legendDisplay = 'stacked',
    store = null,
    gridColspan
  }: Props = $props();

  const hasInteractions = Boolean(interactions.length > 0);

  const responsiveVars = new ResponsiveVars()
    .addFluid('spacer-1', toFluidVarInput(6, 6))
    .addFluid('spacer-2', toFluidVarInput(9, 8))
    .addFluid('spacer-3', toFluidVarInput(12, 12))
    .addFluid('spacer-4', toFluidVarInput(24, 24))
    .addResponsive('font-size', { min: 16, lg: 24 })
    .toCSS();
</script>

{#if categories && colorPalette}
  <div class={cx('wrapper', responsiveVars, isChartLarge(gridColspan) && 'large-grid')}>
    {#each categories as category, index}
      {@const color = colorPalette.colors[index]}
      <div class:stacked={legendDisplay === 'stacked'} data-color={color?.id}>
        <div
          class="item"
          role="tooltip"
          class:disabled={!hasInteractions}
          class:selected={$store?.selected && $store?.selected.includes(color?.id)}
          onmouseover={() => store?.triggerInteraction(interactions, [color?.id])}
          onfocus={() => store?.triggerInteraction(interactions, [color?.id])}
          onmouseout={() => store?.resetInteraction(interactions)}
          onblur={() => store?.resetInteraction(interactions)}
        >
          <span
            class="color"
            class:gradient={color?.colors}
            style={`
            --color: ${color?.color};
            --color-1: ${color?.colors?.[0]};
            --color-2: ${color?.colors?.[1]};
          `}
          ></span>
          <p>
            {category}
          </p>
        </div>
      </div>
    {/each}
  </div>
{/if}

<style lang="scss">
  @import 'src/scss/media';

  .wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: calc(var(--spacer-1) / 3);

    .disabled {
      pointer-events: none;
    }

    .stacked {
      flex-basis: 100%;
    }

    .item {
      display: flex;
      align-items: center;
      width: fit-content;
      gap: var(--spacer-2);
      padding: var(--spacer-1);
      cursor: pointer;
      transition:
        background-color 0.1s ease-in,
        color 0.1s ease-in;
      border-radius: 100px;

      &.selected,
      &:hover {
        background-color: var(--twist-color-gray-0);
        color: var(--twist-color-gray-100);

        .color {
          border-color: #000000;
        }
      }
    }
  }

  .color {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 20px;
    aspect-ratio: 1/1;
    background-color: var(--color);
    border: 1px solid var(--color);
    transition: border 0.1s ease-in;

    &.gradient {
      background: linear-gradient(90deg, var(--color-1) 0%, var(--color-2) 100%);
    }
  }

  p {
    display: inline-block;
    font-family: var(--twist-font-family-pilat-condensed);
    font-size: var(--font-size);
    font-weight: 600;
    line-height: 100%;
  }

  .large-grid {
    .item {
      padding: calc(var(--spacer-1) * 0.5) var(--spacer-2);
    }
  }
</style>
